import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, UserPlus, Trash2, Edit2, Shield, Eye, MoreVertical, Search, X, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from 'sonner';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

const ROLE_COLORS = {
  admin: 'bg-red-500/20 text-red-400 border-red-500/30',
  portfolio_director: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  project_manager: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  viewer: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  custom: 'bg-amber-500/20 text-amber-400 border-amber-500/30'
};

const ROLE_ICONS = {
  admin: Shield,
  portfolio_director: Users,
  project_manager: Edit2,
  viewer: Eye,
  custom: Shield
};

export default function UserManagement() {
  const { user, isAdmin, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'viewer', assigned_projects: [] });
  const [editForm, setEditForm] = useState({ role: '', assigned_projects: [] });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, projectsRes] = await Promise.all([
        api.get('/organization/users'),
        api.get('/organization/roles'),
        api.get('/portfolios/portfolio_001/grid')
      ]);
      setUsers(usersRes.data.users || []);
      setRoles(rolesRes.data.roles || []);
      setProjects(projectsRes.data.projects || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.name || !inviteForm.email) {
      toast.error('Name and email are required');
      return;
    }
    try {
      const res = await api.post('/organization/users/invite', inviteForm);
      toast.success(`User invited! Temporary password: ${res.data.temporary_password}`);
      setShowInviteModal(false);
      setInviteForm({ name: '', email: '', role: 'viewer', assigned_projects: [] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite user');
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser) return;
    try {
      await api.put(`/organization/users/${selectedUser.user_id}/role`, editForm);
      toast.success('User role updated');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user?')) return;
    try {
      await api.delete(`/organization/users/${userId}`);
      toast.success('User removed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove user');
    }
  };

  const openEditModal = (u) => {
    setSelectedUser(u);
    setEditForm({ role: u.role, assigned_projects: u.assigned_projects || [] });
    setShowEditModal(true);
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleProjectAssignment = (projectId, form, setForm) => {
    const current = form.assigned_projects || [];
    setForm({
      ...form,
      assigned_projects: current.includes(projectId)
        ? current.filter(p => p !== projectId)
        : [...current, projectId]
    });
  };

  if (!isAdmin() && !hasPermission('can_manage_users')) {
    return (
      <DashboardLayout>
        <div className="flex-1 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white">Access Denied</h2>
            <p className="text-gray-400 mt-2">You don't have permission to manage users.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-white">Team Members</h1>
                <p className="text-gray-400 mt-1">{users.length} users in organization</p>
              </div>
              
              <Button
                data-testid="invite-user-btn"
                onClick={() => setShowInviteModal(true)}
                className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            </div>
            
            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                data-testid="user-search-input"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="pl-10 bg-[#1F2937] border-[#3A3F4A] text-white w-full max-w-md"
              />
            </div>
            
            {/* Users Grid */}
            {loading ? (
              <div className="text-center text-gray-400 py-12">Loading users...</div>
            ) : (
              <div className="grid gap-4">
                {filteredUsers.map(u => {
                  const RoleIcon = ROLE_ICONS[u.role] || Eye;
                  return (
                    <motion.div
                      key={u.user_id}
                      data-testid={`user-card-${u.user_id}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-[#0f1629] border border-[#3A3F4A] rounded-lg p-4 flex items-center justify-between hover:border-[#1E4E8C]/30 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E4E8C] to-[#143A6B] flex items-center justify-center text-white font-semibold">
                          {u.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-white">{u.name}</h3>
                            {u.user_id === user?.user_id && (
                              <span className="text-xs px-2 py-0.5 bg-[#0E7C6B]/20 text-[#0E7C6B] rounded-full">You</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-400">{u.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${ROLE_COLORS[u.role] || ROLE_COLORS.viewer}`}>
                          <RoleIcon className="w-3.5 h-3.5" />
                          <span className="text-sm font-medium capitalize">{u.role?.replace('_', ' ')}</span>
                        </div>
                        
                        {u.assigned_projects?.length > 0 && (
                          <span className="text-xs text-gray-400">
                            {u.assigned_projects.length} projects
                          </span>
                        )}
                        
                        {u.user_id !== user?.user_id && (
                          <div className="flex gap-2">
                            <Button
                              data-testid={`edit-user-${u.user_id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(u)}
                              className="text-gray-400 hover:text-white"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`remove-user-${u.user_id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveUser(u.user_id)}
                              className="text-gray-400 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
                
                {filteredUsers.length === 0 && (
                  <div className="text-center text-gray-400 py-12">
                    {search ? 'No users match your search' : 'No users found'}
                  </div>
                )}
              </div>
            )}
          </div>
      
      {/* Invite Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowInviteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#0f1629] border border-[#3A3F4A] rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-[#3A3F4A] flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">Invite User</h2>
                <button onClick={() => setShowInviteModal(false)} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <Label className="text-gray-300">Full Name</Label>
                  <Input
                    data-testid="invite-name-input"
                    value={inviteForm.name}
                    onChange={e => setInviteForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Jane Doe"
                    className="mt-1 bg-[#1F2937] border-[#3A3F4A] text-white"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Email</Label>
                  <Input
                    data-testid="invite-email-input"
                    type="email"
                    value={inviteForm.email}
                    onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="jane@company.com"
                    className="mt-1 bg-[#1F2937] border-[#3A3F4A] text-white"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Role</Label>
                  <select
                    data-testid="invite-role-select"
                    value={inviteForm.role}
                    onChange={e => setInviteForm(p => ({ ...p, role: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 bg-[#1F2937] border border-[#3A3F4A] rounded-md text-white"
                  >
                    {roles.filter(r => r.type === 'system').map(r => (
                      <option key={r.role_id} value={r.role_id}>{r.name}</option>
                    ))}
                  </select>
                </div>
                
                {['project_manager', 'viewer'].includes(inviteForm.role) && (
                  <div>
                    <Label className="text-gray-300">Assigned Projects</Label>
                    <p className="text-xs text-gray-500 mb-2">Select projects this user can access</p>
                    <div className="max-h-40 overflow-y-auto space-y-2 border border-[#3A3F4A] rounded-md p-2">
                      {projects.slice(0, 10).map(p => (
                        <label key={p.project_id} className="flex items-center gap-2 cursor-pointer hover:bg-[#3A3F4A]/50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={inviteForm.assigned_projects.includes(p.project_id)}
                            onChange={() => toggleProjectAssignment(p.project_id, inviteForm, setInviteForm)}
                            className="rounded border-gray-600"
                          />
                          <span className="text-sm text-gray-300">{p.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-6 border-t border-[#3A3F4A] flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setShowInviteModal(false)}>Cancel</Button>
                <Button
                  data-testid="confirm-invite-btn"
                  onClick={handleInvite}
                  className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90"
                >
                  Send Invite
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Edit Role Modal */}
      <AnimatePresence>
        {showEditModal && selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#0f1629] border border-[#3A3F4A] rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-[#3A3F4A] flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-white">Edit User Role</h2>
                  <p className="text-sm text-gray-400 mt-1">{selectedUser.name} ({selectedUser.email})</p>
                </div>
                <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <Label className="text-gray-300">Role</Label>
                  <select
                    data-testid="edit-role-select"
                    value={editForm.role}
                    onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 bg-[#1F2937] border border-[#3A3F4A] rounded-md text-white"
                  >
                    {roles.filter(r => r.type === 'system').map(r => (
                      <option key={r.role_id} value={r.role_id}>{r.name}</option>
                    ))}
                  </select>
                </div>
                
                {['project_manager', 'viewer'].includes(editForm.role) && (
                  <div>
                    <Label className="text-gray-300">Assigned Projects</Label>
                    <p className="text-xs text-gray-500 mb-2">Select projects this user can access</p>
                    <div className="max-h-40 overflow-y-auto space-y-2 border border-[#3A3F4A] rounded-md p-2">
                      {projects.slice(0, 10).map(p => (
                        <label key={p.project_id} className="flex items-center gap-2 cursor-pointer hover:bg-[#3A3F4A]/50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={editForm.assigned_projects.includes(p.project_id)}
                            onChange={() => toggleProjectAssignment(p.project_id, editForm, setEditForm)}
                            className="rounded border-gray-600"
                          />
                          <span className="text-sm text-gray-300">{p.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-6 border-t border-[#3A3F4A] flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setShowEditModal(false)}>Cancel</Button>
                <Button
                  data-testid="confirm-edit-btn"
                  onClick={handleUpdateRole}
                  className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90"
                >
                  Update Role
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
}
